import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Field,
  LockIcon,
  Logo,
  Note,
  Panel,
  ShieldIcon,
  Spinner,
} from '@wyocspay/ui';
import { dateTime } from './format.js';
import { ApiError, api, type FeatureState, type FeatureRequest } from './api.js';

/**
 * What this merchant can do, and how to ask for what they cannot.
 *
 * The gating is honest about itself: a locked capability says why it is
 * locked and what would unlock it, rather than simply not appearing.
 */
export function FeaturesView() {
  const [features, setFeatures] = useState<FeatureState[] | null>(null);
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState<FeatureState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [f, r] = await Promise.all([api.features(), api.featureRequests()]);
      setFeatures(f.data);
      setRequests(r.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Spinner label="Loading capabilities…" />;
  if (error) return <Note tone="danger">{error}</Note>;
  if (!features) return null;

  const open = requests.filter((r) => ['SUBMITTED', 'INFO_REQUESTED'].includes(r.status));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="How access works here">
        Capabilities unlock in two ways. Some open automatically once <strong>Stripe</strong> has
        verified your business - the platform does not make that judgement. Others need a decision
        from the platform team, because they move money in ways that are hard to reverse. Ask for
        those below and we will come back to you.
      </Note>

      {open.length > 0 ? (
        <Panel title="Your open requests" padded={false}>
          {open.map((r) => (
            <RequestThread key={r.id} request={r} onChanged={load} />
          ))}
        </Panel>
      ) : null}

      {requesting ? (
        <RequestForm
          feature={requesting}
          onCancel={() => setRequesting(null)}
          onDone={async () => {
            setRequesting(null);
            await load();
          }}
        />
      ) : null}

      <div style={{ display: 'grid', gap: 12 }}>
        {features.map((f) => (
          <Panel key={f.key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 15 }}>{f.label}</strong>
                  {f.available ? (
                    <Badge tone="ok">ENABLED</Badge>
                  ) : (
                    <Badge tone={f.gate === 'approval' ? 'warn' : 'pending'}>
                      <LockIcon size={11} /> {f.gate === 'approval' ? 'NEEDS APPROVAL' : 'NEEDS VERIFICATION'}
                    </Badge>
                  )}
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--wc-muted)' }}>{f.description}</p>

                {f.blockedReason ? (
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--wc-orange)' }}>{f.blockedReason}</p>
                ) : null}

                {f.available && Object.keys(f.conditions).length > 0 ? (
                  <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--wc-faint)' }}>
                    Conditions:{' '}
                    <span className="wc-num">
                      {Object.entries(f.conditions).map(([k, v]) => `${k}=${String(v)}`).join(', ')}
                    </span>
                  </p>
                ) : null}
              </div>

              {/*
                `requestable` is the server's answer rather than this panel's.
                It was derived here from the gate and whether a request was
                already open, which misses the case where asking cannot lead
                anywhere at all - a capability the card schemes refuse in this
                merchant's country is one no administrator can approve, and
                offering the form produces a request that is declined for a
                reason nobody involved can do anything about.
              */}
              {f.requestable ? (
                <button
                  type="button"
                  className={`wc-btn wc-btn-sm${f.request ? ' wc-btn-secondary' : ''}`}
                  onClick={() => setRequesting(f)}
                >
                  {f.request ? 'Request again' : 'Request access'}
                </button>
              ) : null}
            </div>
          </Panel>
        ))}
      </div>

      {requests.filter((r) => !['SUBMITTED', 'INFO_REQUESTED'].includes(r.status)).length > 0 ? (
        <Panel title="Past requests" padded={false}>
          {requests
            .filter((r) => !['SUBMITTED', 'INFO_REQUESTED'].includes(r.status))
            .map((r) => (
              <RequestThread key={r.id} request={r} onChanged={load} />
            ))}
        </Panel>
      ) : null}
    </div>
  );
}

function RequestForm({
  feature,
  onCancel,
  onDone,
}: {
  feature: FeatureState;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [justification, setJustification] = useState('');
  const [intendedUse, setIntendedUse] = useState('');
  const [volume, setVolume] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.requestFeature({
        feature: feature.key,
        justification,
        intended_use: intendedUse,
        expected_volume: volume || undefined,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={`Request: ${feature.label}`}>
      {feature.approvalRationale ? (
        <div style={{ marginBottom: 16 }}>
          <Note title="Why we ask">{feature.approvalRationale}</Note>
        </div>
      ) : null}

      <Field label="Why do you need this?" hint="A sentence or two is enough.">
        <textarea
          className="wc-input"
          rows={3}
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          style={{ resize: 'vertical' }}
        />
      </Field>

      <Field label="What will you use it for?">
        <textarea
          className="wc-input"
          rows={3}
          value={intendedUse}
          onChange={(e) => setIntendedUse(e.target.value)}
          style={{ resize: 'vertical' }}
        />
      </Field>

      {feature.questions && feature.questions.length > 0 ? (
        <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--wc-muted)' }}>
          Please also cover:
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {feature.questions.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      ) : null}

      <Field label="Expected volume (optional)">
        <input className="wc-input" value={volume} onChange={(e) => setVolume(e.target.value)} placeholder="e.g. 400 invoices a month" />
      </Field>

      {error ? <div style={{ marginBottom: 12 }}><Note tone="danger">{error}</Note></div> : null}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="wc-btn"
          onClick={submit}
          disabled={busy || justification.trim().length < 20 || intendedUse.trim().length < 20}
        >
          {busy ? 'Sending…' : 'Send request'}
        </button>
        <button type="button" className="wc-btn wc-btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </Panel>
  );
}

function RequestThread({ request, onChanged }: { request: FeatureRequest; onChanged: () => void }) {
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const tone =
    request.status === 'APPROVED' ? 'ok'
    : request.status === 'DENIED' ? 'fail'
    : request.status === 'INFO_REQUESTED' ? 'warn'
    : 'pending';

  return (
    <div style={{ padding: 16, borderBottom: '1px solid var(--wc-border)' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <strong style={{ fontSize: 14.5 }}>{request.feature_label}</strong>
        <Badge tone={tone}>{request.status.replace(/_/g, ' ')}</Badge>
        <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>{dateTime(request.created_at)}</span>
      </div>

      {request.messages.length > 0 ? (
        <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
          {request.messages.map((m) => (
            <div
              key={m.id}
              style={{
                padding: '8px 12px',
                background: m.author_role === 'ADMIN' ? 'var(--wc-amber-tint)' : m.author_role === 'SYSTEM' ? 'var(--wc-grey-tint)' : 'var(--wc-blue-tint)',
                borderLeft: `3px solid ${m.author_role === 'ADMIN' ? 'var(--wc-amber)' : m.author_role === 'SYSTEM' ? 'var(--wc-grey)' : 'var(--wc-blue)'}`,
                fontSize: 13.5,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--wc-muted)', marginBottom: 2 }}>
                {m.author_role === 'ADMIN' ? 'PLATFORM TEAM' : m.author_role === 'SYSTEM' ? 'DECISION' : 'YOU'}
              </div>
              {m.body}
            </div>
          ))}
        </div>
      ) : null}

      {request.status === 'INFO_REQUESTED' ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <textarea
            className="wc-input"
            rows={2}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Answer the platform team…"
            style={{ resize: 'vertical' }}
          />
          <button
            type="button"
            className="wc-btn wc-btn-sm"
            disabled={busy || reply.trim().length === 0}
            onClick={async () => {
              setBusy(true);
              try {
                await api.replyToRequest(request.id, reply);
                setReply('');
                onChanged();
              } finally {
                setBusy(false);
              }
            }}
          >
            Reply
          </button>
        </div>
      ) : null}

      {request.status === 'APPROVED' && request.conditions ? (
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--wc-green)' }}>
          <ShieldIcon size={12} /> Granted with conditions:{' '}
          <span className="wc-num">
            {Object.entries(request.conditions).map(([k, v]) => `${k}=${String(v)}`).join(', ')}
          </span>
        </p>
      ) : null}
    </div>
  );
}

/** Sign-up by invitation. There is no self-registration in this platform. */
export function AcceptInvite({ token }: { token: string }) {
  const [invite, setInvite] = useState<{ email: string; merchant: { name: string }; expires_at: string } | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setInvite(await api.invite(token));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const strong = password.length >= 12 && password === confirm;

  if (loading) {
    return <Centre><Spinner label="Checking invitation…" /></Centre>;
  }
  if (error && !invite) {
    return <Centre><Note tone="danger" title="This invitation cannot be used">{error}</Note></Centre>;
  }
  if (done) {
    return (
      <Centre>
        <Note tone="ok" title="Account created">
          You can now sign in with {invite?.email}.
          <div style={{ marginTop: 12 }}>
            <a className="wc-btn" href="/" style={{ textDecoration: 'none' }}>Go to sign in</a>
          </div>
        </Note>
      </Centre>
    );
  }

  return (
    <Centre>
      <Panel title={`Join ${invite?.merchant.name ?? ''}`}>
        <p style={{ marginTop: 0, fontSize: 13.5, color: 'var(--wc-muted)' }}>
          You are setting the password for <strong>{invite?.email}</strong>. Nobody at the platform,
          including administrators, ever sees it.
        </p>

        <Field label="Your name">
          <input className="wc-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </Field>
        <Field label="Password" hint="At least 12 characters.">
          <input className="wc-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </Field>
        <Field label="Confirm password">
          <input className="wc-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </Field>

        {password.length > 0 && password.length < 12 ? (
          <div style={{ marginBottom: 12 }}><Note tone="warn">Password must be at least 12 characters.</Note></div>
        ) : confirm.length > 0 && password !== confirm ? (
          <div style={{ marginBottom: 12 }}><Note tone="warn">Passwords do not match.</Note></div>
        ) : null}

        {error ? <div style={{ marginBottom: 12 }}><Note tone="danger">{error}</Note></div> : null}

        <button
          type="button"
          className="wc-btn"
          style={{ width: '100%' }}
          disabled={busy || !strong || name.trim().length < 2}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await api.acceptInvite(token, name, password);
              setDone(true);
            } catch (err) {
              setError(err instanceof ApiError ? err.message : String(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Creating…' : 'Create account'}
        </button>
      </Panel>
    </Centre>
  );
}

function Centre({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: 'var(--wc-blue-header)', color: '#fff', padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Logo height={56} />
        </div>
      </header>
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 460 }}>{children}</div>
      </main>
    </div>
  );
}
